import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { MapContainer, TileLayer } from 'react-leaflet';
import { useLocation } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet.markercluster';
import { renderToString } from 'react-dom/server';
import { ServiceLocationInfo } from '../../types/firestore';
import { Badge, Button, Spinner, Alert } from '../common';
import { useAuth } from '../../contexts/AuthContext';
import { canShowRepairRequestCtaForServiceProvider } from '../../utils/accessControl';
import {
  getBestMatchingSpecialization,
  getServiceSmartMatchReason,
  getServiceCity,
  isRenderableServicePosition,
  type ServiceDiscoveryIssueType,
} from '../../utils/serviceDiscovery';
import type { SmartSearchSelection } from '../../utils/smartSearch';
import { resolveServiceAvailabilityStatus } from '../../utils/businessHours';
import { StarIcon } from '@heroicons/react/24/solid';
import {
  buildServiceProviderPopupImageMarkup,
  getServiceProviderRequestHref,
} from '../../utils/serviceProviderPopupMarkup';
import { getServiceProviderProfilePath } from '../../utils/serviceProviderProfilePath';
import ServiceFulfillmentSummary from '../ServiceProvider/ServiceFulfillmentSummary';
import {
  formatServiceHourlyRateLabel,
  getPublicServiceHourlyRate,
} from '../../utils/servicePricing';

const GEOLOCATION_ZOOM_LEVEL = 13;
const MAP_MIN_ZOOM = 3;
const MAP_MAX_ZOOM = 19;
const POLAND_BOUNDS = L.latLngBounds(L.latLng(48.95, 13.8), L.latLng(54.95, 24.2));
const MOBILE_BREAKPOINT = '(max-width: 768px)';
const POLAND_FALLBACK_MAX_ZOOM = { mobile: 7.8, desktop: 8 };
const SINGLE_RESULT_MAX_ZOOM = { mobile: 12, desktop: 13 };
const MULTI_RESULT_MAX_ZOOM = { mobile: 14, desktop: 15 };

type SavedViewport = {
  signature: string;
  center: L.LatLngTuple;
  zoom: number;
};

let savedViewport: SavedViewport | null = null;

interface MapComponentProps {
  markersData: ServiceLocationInfo[];
  loading: boolean;
  error: string | null;
  searchQuery?: string;
  smartSelection?: SmartSearchSelection | null;
  issueType?: ServiceDiscoveryIssueType;
  fitContextKey?: string;
  resultsOverlayLabel?: string;
  emptyState?: {
    title: string;
    message: string;
    actionLabel?: string;
    onAction?: () => void;
  };
}

const LocateControl: React.FC<{ onClick: () => void; isLoading: boolean; className?: string }> = ({
  onClick,
  isLoading,
  className = '',
}) => (
  <Button
    variant="neutral"
    size="md"
    onClick={onClick}
    disabled={isLoading}
    className={`rs-map-floating-control !rounded-lg !p-2.5 !text-primary-light-300 ${className}`}
    aria-label={isLoading ? 'Lokalizowanie...' : 'Znajdź moją lokalizację'}
  >
    {isLoading ? (
      <Spinner size="sm" color="primary" />
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-5 h-5 text-ratuj-300"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
        />
      </svg>
    )}
  </Button>
);

const customIcon = L.divIcon({
  className: 'custom-marker-icon relative',
  html: `<div class="relative flex items-center justify-center" style="width: 30px; height: 40px;">
          <svg class="absolute w-8 h-8 text-ratuj-400 drop-shadow-lg" viewBox="0 0 24 24" fill="currentColor">
            <path fill-rule="evenodd" d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" />
          </svg>
         </div>`,
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  popupAnchor: [0, -42],
});

const formatReviewNoun = (count: number): string => {
  if (count === 1) return 'opinia';
  if (count < 5) return 'opinie';
  return 'opinii';
};

const formatServiceCountLabel = (count: number): string => {
  if (count === 0) return '0 wyników';
  if (count === 1) return '1 serwis';
  if (count < 5) return `${count} serwisy`;
  return `${count} serwisów`;
};

const renderRatingStars = (rating = 0, count = 0, extraMeta?: string | null) => {
  if (!count) {
    return (
      <div
        className="flex flex-wrap items-center gap-x-1.5 gap-y-1"
        data-testid="service-popup-rating-meta"
      >
        <span className="text-xs text-primary-light-400 italic">Brak opinii</span>
        {extraMeta ? (
          <>
            <span className="text-xs text-primary-light-500">·</span>
            <span
              className="text-[11px] font-medium leading-tight text-primary-light-300"
              data-testid="service-popup-hourly-rate"
            >
              {extraMeta}
            </span>
          </>
        ) : null}
      </div>
    );
  }
  const validRating = Math.max(0, Math.min(rating, 5));
  const fullStars = Math.floor(validRating);
  const hasHalfStar = validRating % 1 >= 0.5;
  return (
    <div
      className="flex flex-wrap items-center gap-x-1 gap-y-1"
      data-testid="service-popup-rating-meta"
    >
      {[...Array(5)].map((_, index) => (
        <StarIcon
          key={index}
          className={`w-3.5 h-3.5 ${
            index < fullStars
              ? 'text-gold-300'
              : index === fullStars && hasHalfStar
                ? 'text-gold-300/60'
                : 'text-primary-dark-300/70'
          }`}
        />
      ))}
      <span className="ml-1 text-xs font-medium text-primary-light-200">{rating.toFixed(1)}/5</span>
      <span className="text-xs text-primary-light-400 ml-1">
        ({count} {formatReviewNoun(count)})
      </span>
      {extraMeta ? (
        <>
          <span className="text-xs text-primary-light-500">·</span>
          <span
            className="text-[11px] font-medium leading-tight text-primary-light-300"
            data-testid="service-popup-hourly-rate"
          >
            {extraMeta}
          </span>
        </>
      ) : null}
    </div>
  );
};

export const MarkerPopupContent: React.FC<{
  marker: ServiceLocationInfo;
  searchQuery?: string;
  smartSelection?: SmartSearchSelection | null;
  issueType?: ServiceDiscoveryIssueType;
  showRequestCta?: boolean;
}> = ({
  marker,
  searchQuery = '',
  smartSelection = null,
  issueType = null,
  showRequestCta = false,
}) => {
  const city = getServiceCity(marker) || 'Brak miasta';
  const reviewsCount = marker.reviewsCount ?? marker.numberOfRatings ?? 0;
  const averageRating = marker.averageRating ?? 0;
  const liveStatus = resolveServiceAvailabilityStatus(marker);
  const matchedSpecialization = getBestMatchingSpecialization(marker, searchQuery);
  const matchReason = getServiceSmartMatchReason(marker, smartSelection, issueType);
  const publicHourlyRate = getPublicServiceHourlyRate(marker);
  const hourlyRateLabel = publicHourlyRate
    ? formatServiceHourlyRateLabel(publicHourlyRate.amount)
    : null;

  return (
    <div className="w-[20rem] max-w-[calc(100vw-2rem)]">
      <div className="flex items-start gap-3">
        <div
          className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-primary-dark-300 shadow-card"
          dangerouslySetInnerHTML={{
            __html: buildServiceProviderPopupImageMarkup(marker),
          }}
        />
        <div className="min-w-0 flex-1 space-y-1">
          <h3
            className="line-clamp-2 break-words text-[15px] font-semibold leading-snug text-primary-light-100 [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden [overflow-wrap:anywhere]"
            title={marker.serviceName || 'Brak nazwy'}
          >
            {marker.serviceName || 'Brak nazwy'}
          </h3>
          <p
            className="break-words text-xs text-primary-light-300 [display:-webkit-box] [-webkit-line-clamp:1] [-webkit-box-orient:vertical] overflow-hidden [overflow-wrap:anywhere]"
            title={city}
          >
            {city}
          </p>
          <div
            className={`block max-w-full text-xs font-medium leading-snug ${
              liveStatus.state === 'open'
                ? 'text-status-success-500'
                : liveStatus.state === 'temporarily_unavailable'
                  ? 'text-status-warning-500'
                  : liveStatus.state === 'closed'
                    ? 'text-primary-light-200'
                    : 'text-primary-light-400'
            }`}
            data-testid="service-popup-business-hours"
            title={liveStatus.summaryText}
          >
            <span className="truncate">{liveStatus.summaryText}</span>
          </div>
        </div>
      </div>
      <ServiceFulfillmentSummary provider={marker} variant="popup" className="mt-2" />
      {matchedSpecialization && (
        <div className="mt-2 min-w-0">
          <Badge
            variant="primary"
            size="sm"
            className="inline-flex max-w-full truncate !px-2.5 !py-1 text-[11px] leading-tight"
          >
            Najlepsze dopasowanie: {matchedSpecialization}
          </Badge>
        </div>
      )}
      {matchReason && (
        <p className="mt-2 truncate text-xs text-primary-light-300">Pasuje: {matchReason}</p>
      )}
      <div className="mt-2">{renderRatingStars(averageRating, reviewsCount, hourlyRateLabel)}</div>
      {marker.isTestData && (
        <Badge variant="neutral" size="sm" className="mt-2">
          Profil testowy - podglad demonstracyjny
        </Badge>
      )}

      <div className="mx-auto mt-3 grid w-full max-w-52 grid-cols-1 gap-1.5">
        {showRequestCta && (
          <a
            href={getServiceProviderRequestHref(marker.uid)}
            className="inline-flex min-h-[40px] w-full items-center justify-center rounded-lg bg-ratuj-400 px-3 py-2 text-center text-sm font-semibold text-white no-underline shadow-none transition-colors duration-150 hover:bg-ratuj-300"
          >
            Zgłoś naprawę
          </a>
        )}
        <a
          href={getServiceProviderProfilePath(marker.uid)}
          className="rs-popup-secondary-action inline-flex min-h-[40px] w-full items-center justify-center rounded-lg px-3 py-2 text-center text-xs font-semibold text-ratuj-100 no-underline transition-colors duration-150"
        >
          Zobacz profil
        </a>
      </div>
    </div>
  );
};

const MapComponent: React.FC<MapComponentProps> = ({
  markersData,
  loading,
  error,
  searchQuery = '',
  smartSelection = null,
  issueType = null,
  fitContextKey = '',
  resultsOverlayLabel,
  emptyState,
}) => {
  const location = useLocation();
  const { currentUser, userProfile, claims } = useAuth();
  const isInitialMobileViewport =
    typeof window !== 'undefined' && window.matchMedia(MOBILE_BREAKPOINT).matches;
  const defaultPosition = useMemo<L.LatLngExpression>(() => [52.2, 19.0], []);
  const defaultZoom = useMemo(
    () => (isInitialMobileViewport ? 7.2 : 6.8),
    [isInitialMobileViewport]
  );
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locateError, setLocateError] = useState('');
  const [clusterUnavailable, setClusterUnavailable] = useState(false);
  const mapShellRef = useRef<HTMLDivElement | null>(null);
  const markerLayerRef = useRef<L.FeatureGroup | L.MarkerClusterGroup | null>(null);
  const viewportTaskIdRef = useRef(0);
  const viewportRaf1Ref = useRef<number | null>(null);
  const viewportRaf2Ref = useRef<number | null>(null);
  const viewportTimeoutRef = useRef<number | null>(null);
  const hasInitialViewportRef = useRef(false);
  const lastAutoFitSignatureRef = useRef<string>('');
  const activeFitSignatureRef = useRef<string>('');
  const renderableMarkers = useMemo(
    () => markersData.filter((marker) => isRenderableServicePosition(marker.position)),
    [markersData]
  );
  const canShowRequestCtaForMarker = useCallback(
    (marker: ServiceLocationInfo) =>
      canShowRepairRequestCtaForServiceProvider(
        userProfile,
        claims,
        currentUser?.uid || null,
        marker.uid,
        marker.isTestData === true
      ),
    [claims, currentUser?.uid, userProfile]
  );
  const markersSignature = useMemo(
    () =>
      renderableMarkers
        .map((marker) => {
          const [lat, lng] = marker.position;
          return `${marker.uid}:${lat.toFixed(4)}:${lng.toFixed(4)}`;
        })
        .sort()
        .join('|'),
    [renderableMarkers]
  );
  const fitSignature = useMemo(
    () => `${markersSignature}::${fitContextKey}`,
    [markersSignature, fitContextKey]
  );
  const resultsPillLabel = useMemo(() => {
    if (resultsOverlayLabel) {
      return resultsOverlayLabel;
    }
    if (loading) {
      return 'Ładowanie wyników';
    }
    return formatServiceCountLabel(renderableMarkers.length);
  }, [loading, renderableMarkers.length, resultsOverlayLabel]);

  const ensureClusterLayer = useCallback((map: L.Map): L.FeatureGroup | L.MarkerClusterGroup => {
    if (!markerLayerRef.current) {
      try {
        const clusterLayer = (L as any).markerClusterGroup({
          chunkedLoading: true,
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          disableClusteringAtZoom: 15,
          maxClusterRadius: 45,
        }) as L.MarkerClusterGroup;
        markerLayerRef.current = clusterLayer;
        setClusterUnavailable(false);
      } catch (clusterError) {
        console.error('Cluster init failed, falling back to plain markers.', clusterError);
        markerLayerRef.current = L.featureGroup();
        setClusterUnavailable(true);
      }
    }
    if (!map.hasLayer(markerLayerRef.current)) {
      markerLayerRef.current.addTo(map);
    }
    return markerLayerRef.current;
  }, []);

  const invalidateMapSize = useCallback(() => {
    if (!mapInstance) return;
    window.requestAnimationFrame(() => {
      mapInstance.invalidateSize({ pan: false, animate: false });
    });
  }, [mapInstance]);

  const getRenderableBounds = useCallback(
    (markers: ServiceLocationInfo[]): L.LatLngBounds | null => {
      if (markers.length === 0) return null;
      const markerLayer = markerLayerRef.current;
      if (markerLayer) {
        const boundsFromLayer = markerLayer.getBounds?.();
        if (boundsFromLayer?.isValid()) {
          return boundsFromLayer;
        }
      }
      const boundsFromData = L.latLngBounds(markers.map((marker) => marker.position));
      return boundsFromData.isValid() ? boundsFromData : null;
    },
    []
  );

  const fitMapToBounds = useCallback(
    (markers: ServiceLocationInfo[], options?: { manual?: boolean }) => {
      if (!mapInstance) return;
      const isMobileViewport = window.matchMedia(MOBILE_BREAKPOINT).matches;
      const isManualFit = options?.manual === true;
      const paddingTopLeft = isMobileViewport
        ? isManualFit
          ? L.point(8, 12)
          : L.point(10, 14)
        : isManualFit
          ? L.point(10, 12)
          : L.point(12, 14);
      const paddingBottomRight = isMobileViewport
        ? isManualFit
          ? L.point(24, 24)
          : L.point(28, 26)
        : isManualFit
          ? L.point(20, 20)
          : L.point(24, 24);

      if (markers.length === 0) {
        if (options?.manual) return;
        mapInstance.fitBounds(POLAND_BOUNDS, {
          paddingTopLeft,
          paddingBottomRight,
          maxZoom: isMobileViewport
            ? POLAND_FALLBACK_MAX_ZOOM.mobile
            : POLAND_FALLBACK_MAX_ZOOM.desktop,
          animate: !isManualFit,
        });
        return;
      }

      if (markers.length === 1) {
        const singleResultZoom = isMobileViewport
          ? SINGLE_RESULT_MAX_ZOOM.mobile
          : SINGLE_RESULT_MAX_ZOOM.desktop;
        mapInstance.setView(markers[0].position, Math.min(singleResultZoom, MAP_MAX_ZOOM), {
          animate: !isManualFit,
        });
        return;
      }

      try {
        const bounds = getRenderableBounds(markers);
        if (!bounds) {
          mapInstance.setView(defaultPosition, defaultZoom, { animate: !isManualFit });
          return;
        }
        const multiResultMaxZoom = isMobileViewport
          ? MULTI_RESULT_MAX_ZOOM.mobile
          : MULTI_RESULT_MAX_ZOOM.desktop;
        mapInstance.fitBounds(bounds, {
          paddingTopLeft,
          paddingBottomRight,
          maxZoom: Math.min(multiResultMaxZoom, MAP_MAX_ZOOM),
          animate: !isManualFit,
        });
      } catch (fitError) {
        console.error('Błąd dopasowania mapy do wyników:', fitError);
        mapInstance.setView(defaultPosition, defaultZoom, { animate: !isManualFit });
      }
    },
    [mapInstance, getRenderableBounds, defaultPosition, defaultZoom]
  );

  const cancelPendingViewportJob = useCallback(() => {
    if (viewportRaf1Ref.current) window.cancelAnimationFrame(viewportRaf1Ref.current);
    if (viewportRaf2Ref.current) window.cancelAnimationFrame(viewportRaf2Ref.current);
    if (viewportTimeoutRef.current) window.clearTimeout(viewportTimeoutRef.current);
    viewportRaf1Ref.current = null;
    viewportRaf2Ref.current = null;
    viewportTimeoutRef.current = null;
  }, []);

  const scheduleAutoFit = useCallback(
    (signature: string) => {
      if (!mapInstance) return;
      const taskId = ++viewportTaskIdRef.current;
      cancelPendingViewportJob();

      viewportRaf1Ref.current = window.requestAnimationFrame(() => {
        if (taskId !== viewportTaskIdRef.current) return;
        invalidateMapSize();
        viewportRaf2Ref.current = window.requestAnimationFrame(() => {
          if (taskId !== viewportTaskIdRef.current) return;
          viewportTimeoutRef.current = window.setTimeout(() => {
            if (taskId !== viewportTaskIdRef.current) return;
            invalidateMapSize();
            fitMapToBounds(renderableMarkers);
            hasInitialViewportRef.current = true;
            lastAutoFitSignatureRef.current = signature;
          }, 140);
        });
      });
    },
    [cancelPendingViewportJob, fitMapToBounds, invalidateMapSize, mapInstance, renderableMarkers]
  );

  const mapRef = useCallback(
    (mapEl: L.Map | null) => {
      if (!mapEl) return;
      if (typeof mapEl.options.maxZoom !== 'number') {
        mapEl.setMaxZoom(MAP_MAX_ZOOM);
      }
      setMapInstance(mapEl);
      ensureClusterLayer(mapEl);
    },
    [ensureClusterLayer]
  );

  useEffect(() => {
    if (!mapInstance) return;
    const markerLayer = ensureClusterLayer(mapInstance);
    markerLayer.clearLayers();

    renderableMarkers.forEach((markerData) => {
      const showRequestCta = canShowRequestCtaForMarker(markerData);

      L.marker(markerData.position, { icon: customIcon })
        .addTo(markerLayer)
        .bindPopup(
          renderToString(
            <MarkerPopupContent
              marker={markerData}
              searchQuery={searchQuery}
              smartSelection={smartSelection}
              issueType={issueType}
              showRequestCta={showRequestCta}
            />
          ),
          {
            className: 'service-popup',
            minWidth: 284,
            maxWidth: 336,
            autoPan: true,
            keepInView: true,
            autoPanPaddingTopLeft: L.point(16, 86),
            autoPanPaddingBottomRight: L.point(16, 16),
          }
        );
    });
  }, [
    renderableMarkers,
    mapInstance,
    ensureClusterLayer,
    searchQuery,
    smartSelection,
    issueType,
    canShowRequestCtaForMarker,
  ]);

  useEffect(() => {
    activeFitSignatureRef.current = fitSignature;
  }, [fitSignature]);

  useEffect(() => {
    if (!mapInstance) return;

    const persistViewport = () => {
      const signature = activeFitSignatureRef.current;
      if (!signature) return;
      const center = mapInstance.getCenter();
      savedViewport = {
        signature,
        center: [center.lat, center.lng],
        zoom: mapInstance.getZoom(),
      };
    };

    mapInstance.on('moveend', persistViewport);
    mapInstance.on('zoomend', persistViewport);

    return () => {
      mapInstance.off('moveend', persistViewport);
      mapInstance.off('zoomend', persistViewport);
      persistViewport();
    };
  }, [mapInstance]);

  useEffect(() => {
    return () => {
      const map = mapInstance;
      const markerLayer = markerLayerRef.current;
      if (!map || !markerLayer) return;
      markerLayer.clearLayers();
      if (map.hasLayer(markerLayer)) {
        map.removeLayer(markerLayer);
      }
      markerLayerRef.current = null;
    };
  }, [mapInstance]);

  useEffect(() => {
    if (!mapInstance || loading) return;
    if (!hasInitialViewportRef.current && savedViewport?.signature === fitSignature) {
      viewportTaskIdRef.current += 1;
      cancelPendingViewportJob();
      invalidateMapSize();
      mapInstance.setView(savedViewport.center, savedViewport.zoom, { animate: false });
      hasInitialViewportRef.current = true;
      lastAutoFitSignatureRef.current = fitSignature;
      return;
    }

    const shouldAutoFit =
      !hasInitialViewportRef.current || lastAutoFitSignatureRef.current !== fitSignature;
    if (!shouldAutoFit) return;

    scheduleAutoFit(fitSignature);
    return cancelPendingViewportJob;
  }, [
    cancelPendingViewportJob,
    fitSignature,
    invalidateMapSize,
    loading,
    mapInstance,
    scheduleAutoFit,
  ]);

  useEffect(() => {
    if (!mapInstance) return;

    const runInvalidate = () => invalidateMapSize();
    const delayed = window.setTimeout(runInvalidate, 120);

    const onResize = () => runInvalidate();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    const visualViewport = window.visualViewport;
    const onViewportResize = () => runInvalidate();
    visualViewport?.addEventListener('resize', onViewportResize);
    visualViewport?.addEventListener('scroll', onViewportResize);

    const observedElement = mapShellRef.current;
    const observer =
      observedElement && 'ResizeObserver' in window
        ? new ResizeObserver(() => runInvalidate())
        : null;
    if (observer && observedElement) {
      observer.observe(observedElement);
    }

    return () => {
      window.clearTimeout(delayed);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      visualViewport?.removeEventListener('resize', onViewportResize);
      visualViewport?.removeEventListener('scroll', onViewportResize);
      observer?.disconnect();
    };
  }, [mapInstance, invalidateMapSize, location.pathname]);

  useEffect(() => {
    return () => {
      cancelPendingViewportJob();
      viewportTaskIdRef.current += 1;
    };
  }, [cancelPendingViewportJob]);

  const handleLocateClick = useCallback(() => {
    if (!navigator.geolocation) {
      setLocateError('Twoja przeglądarka nie wspiera geolokalizacji.');
      return;
    }

    setIsLocating(true);
    setLocateError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setIsLocating(false);
        if (mapInstance) {
          mapInstance.flyTo(
            [position.coords.latitude, position.coords.longitude],
            GEOLOCATION_ZOOM_LEVEL
          );
        }
      },
      () => {
        setIsLocating(false);
        setLocateError('Nie udało się pobrać Twojej lokalizacji.');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  }, [mapInstance]);

  const handleFitResultsClick = useCallback(() => {
    if (loading || error || renderableMarkers.length === 0) return;
    fitMapToBounds(renderableMarkers, { manual: true });
  }, [error, fitMapToBounds, loading, renderableMarkers]);

  return (
    <div
      ref={mapShellRef}
      className="rs-map-surface relative h-full min-h-0 w-full overflow-hidden"
    >
      {/* Map fills parent shell; parent height is controlled by page layout (not local min-height hacks). */}
      <MapContainer
        ref={mapRef}
        center={defaultPosition}
        zoom={defaultZoom}
        minZoom={MAP_MIN_ZOOM}
        maxZoom={MAP_MAX_ZOOM}
        scrollWheelZoom
        zoomControl={false}
        className="absolute inset-0 bg-primary-dark-200"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={MAP_MAX_ZOOM}
          minZoom={MAP_MIN_ZOOM}
        />
      </MapContainer>

      {clusterUnavailable && !error && (
        <div className="absolute top-14 md:top-16 left-1/2 -translate-x-1/2 z-[1001]">
          <Alert variant="warning" className="text-xs">
            Tryb awaryjny mapy: uproszczone markery.
          </Alert>
        </div>
      )}

      <div className="pointer-events-none absolute left-1/2 top-[max(0.6rem,calc(env(safe-area-inset-top)+0.35rem))] z-[1001] w-[calc(100%-1.25rem)] max-w-fit -translate-x-1/2">
        <button
          type="button"
          className="rs-map-floating-control pointer-events-auto inline-flex min-h-[2rem] max-w-full items-center justify-center rounded-full px-3 py-1 text-xs font-semibold text-primary-light-100 transition-colors duration-150 disabled:cursor-default disabled:text-primary-light-400 md:min-h-[2.15rem] md:px-3.5"
          onClick={handleFitResultsClick}
          disabled={loading || !!error || renderableMarkers.length === 0}
          title={
            loading
              ? 'Ładowanie wyników mapy'
              : renderableMarkers.length === 0
                ? 'Brak wyników do dopasowania'
                : 'Pokaż wszystkie bieżące wyniki na mapie'
          }
          aria-label={
            loading
              ? 'Ładowanie wyników mapy'
              : renderableMarkers.length === 0
                ? `${resultsPillLabel}. Brak wyników do dopasowania`
                : `${resultsPillLabel}. Pokaż wszystkie bieżące wyniki na mapie`
          }
        >
          <span className="truncate">{resultsPillLabel}</span>
        </button>
      </div>

      <div className="absolute bottom-[max(1rem,calc(env(safe-area-inset-bottom)+0.5rem))] right-[max(0.75rem,calc(env(safe-area-inset-right)+0.5rem))] md:bottom-5 md:right-5 z-[1001] flex flex-col items-end gap-2">
        <LocateControl onClick={handleLocateClick} isLoading={isLocating} />
      </div>

      {locateError && (
        <div className="absolute bottom-[max(5.6rem,calc(env(safe-area-inset-bottom)+4.8rem))] left-3 right-[max(4.5rem,calc(env(safe-area-inset-right)+4.25rem))] z-[1001] max-w-xs md:bottom-24 md:left-auto md:right-20">
          <Alert variant="warning" onClose={() => setLocateError('')}>
            {locateError}
          </Alert>
        </div>
      )}

      {emptyState && !loading && !error && (
        <div className="absolute left-3 right-3 top-14 z-[1001] md:left-1/2 md:right-auto md:top-[4.2rem] md:w-[min(24rem,calc(100%-2rem))] md:-translate-x-1/2">
          <div className="rs-map-empty-surface rounded-lg p-3 md:p-3.5">
            <p className="text-sm font-semibold leading-tight text-primary-light-100">
              {emptyState.title}
            </p>
            <p className="mt-1 text-xs leading-5 text-primary-light-300">{emptyState.message}</p>
            {emptyState.actionLabel && emptyState.onAction && (
              <Button
                type="button"
                variant="neutral"
                size="sm"
                className="mt-2 !min-h-[32px] !rounded-md"
                onClick={emptyState.onAction}
              >
                {emptyState.actionLabel}
              </Button>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-primary-dark-100/70 backdrop-blur-sm z-[1002]">
          <Spinner size="lg" color="primary" />
        </div>
      )}

      {error && !loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-primary-dark-100/85 backdrop-blur-sm z-[1002] p-4">
          <Alert variant="error" className="max-w-sm mx-auto">
            {error}
          </Alert>
        </div>
      )}
    </div>
  );
};

export default MapComponent;
